  import { useEffect, useRef, useState } from 'react';
  import { motion, AnimatePresence } from 'framer-motion';
  import { Toaster as Sonner } from 'sonner';
  import { ChevronLeft, ChevronRight } from 'lucide-react';

  import WhiteboardCanvas from '../components/tchrr/WhiteboardCanvas';
  import CTASection from '../components/tchrr/CTASection';
  import ProductHeader from '../components/ProductHeader';
  import MobileProductHeader from '../components/MobileProductHeader';
  import RequestDemoDialog from '../components/tchrr/RequestDemoDialog';
  import TrainingSlotDialog from '../components/tchrr/TrainingSlotDialog';
  import Footer from '../components/Footer';

  const sections = [
    {
      id: 0,
      text: "Classroom whiteboard reimagined!",
      subtext: "Welcome to the future of education",
    },
    {
      id: 1,
      text: "Understands and explains instantly",
      subtext: "AI-powered problem solving in real-time",
    },
    {
      id: 2,
      text: "From equations to visual understanding",
      subtext: "Transform abstract concepts into clear visuals",
    },
    {
      id: 3,
      text: "Simulate concepts, not just solve them",
      subtext: "Interactive physics and real-world simulations",
    },
    {
      id: 4,
      text: "Explore structures in 3D",
      subtext: "Molecular visualization and chemistry tools",
    },
    {
      id: 5,
      text: "Designed to keep students engaged",
      subtext: "Real-time collaboration for the whole class",
    },
    {
      id: 6,
      text: "Bring complex ideas to life",
      subtext: "Cinematic visualizations for deeper understanding",
    },
  ];

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  // Keep the scroll feel consistent across screen sizes (and reduce total scroll vs 250px fixed).
  const getScrollHeightPerSection = () => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    // Give each scene a little more breathing room so the story doesn't feel rushed.
    return clamp(Math.round(vh * 0.32), 220, 320);
  };

  const getPostSectionScrollHeight = () => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    return clamp(Math.round(vh * 0.28), 180, 260);
  };

  export default function TchrrPage() { 
    // Scroll-driven landing experience for /tchrr
    const containerRef = useRef(null);
    const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
    const [isCalendlyTrainingSlotOpen, setIsCalendlyTrainingSlotOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const [scrollHeightPerSection, setScrollHeightPerSection] = useState(getScrollHeightPerSection);
    const [handoffProgress, setHandoffProgress] = useState(0);
    const [isMobileViewport, setIsMobileViewport] = useState(
      typeof window !== 'undefined' ? window.innerWidth <= 799 : false
    );
    const scrollBoundsRef = useRef({ start: 0, sectionEnd: 0, end: 0 });
    const tickingRef = useRef(false);
    const velocityBoostRef = useRef(0);
    const lastScrollYRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const isProgrammaticScrollRef = useRef(false);
    const touchStartXRef = useRef(null);
    const touchEndXRef = useRef(null);

    // When coming from another route, React Router/browser may keep the old scroll position.
    // Force the user back to the top so the scroll-driven landing starts correctly.
    useEffect(() => {
      // Wait for the first paint so Framer Motion measures the final layout.
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);

        // Also cover cases where `documentElement` is the scroll container.
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        setScrolled(window.scrollY > 50);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Initialize scroll scaling once on mount (prevents layout jump after first paint).
    useEffect(() => {
      setScrollHeightPerSection(getScrollHeightPerSection());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const handleResize = () => {
        setIsMobileViewport(window.innerWidth <= 799);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const sectionScrollHeight = Math.max(1, sections.length - 1) * scrollHeightPerSection;
    const postSectionScrollHeight = getPostSectionScrollHeight();
    const totalScrollHeight = sectionScrollHeight + postSectionScrollHeight;

    // Calculate active section based on deterministic window.scrollY mapping.
    // This avoids occasional “slow first/transition” behavior from useScroll measuring offsets.
    useEffect(() => {
      if (isMobileViewport) {
        setHandoffProgress(0);
        return undefined;
      }

      const recalcBounds = () => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const containerTop = rect.top + window.scrollY;

        const start = containerTop; // when target top hits viewport top
        const sectionEnd = start + sectionScrollHeight;
        const end = sectionEnd + postSectionScrollHeight;

        scrollBoundsRef.current = { start, sectionEnd, end };
      };

      const updateFromScroll = () => {
        const { start, sectionEnd, end } = scrollBoundsRef.current;
        const sectionDenom = sectionEnd - start;

        const scrolledFromStart = window.scrollY - start;
        // Keep progression slightly assisted, but avoid the abrupt "jump ahead" feel.
        const mappedScroll =
          scrolledFromStart <= 200
            ? scrolledFromStart
            : 200 + (scrolledFromStart - 200) * 1.2;
        const mappedRaw = sectionDenom <= 0 ? 0 : mappedScroll / sectionDenom;
        const mappedProgress = clamp(mappedRaw, 0, 1);
        const actualRaw = sectionDenom <= 0 ? 0 : scrolledFromStart / sectionDenom;
        const actualProgress = clamp(actualRaw, 0, 1);
        const handoffRaw = end <= sectionEnd ? 0 : (window.scrollY - sectionEnd) / (end - sectionEnd);
        const nextHandoffProgress = clamp(handoffRaw, 0, 1);
        setHandoffProgress(nextHandoffProgress);

        const totalSections = sections.length;
        const maxIdx = totalSections - 1;
        const lastSectionStartProgress = maxIdx > 0 ? (maxIdx - 0.5) / maxIdx : 1;

        // Velocity-aware progress: if the user is scrolling fast, slightly bias toward the next section.
        // This helps keep transitions consistent when browser/frame measurement is a bit “laggy”.
        // Once we reach the last section, stop accelerating and follow actual scroll progress
        // so the CTA handoff tracks the user's pixels more naturally.
        const progressForSections =
          mappedProgress >= lastSectionStartProgress
            ? Math.max(actualProgress, lastSectionStartProgress)
            : mappedProgress;
        const velocityBoost = progressForSections < lastSectionStartProgress ? velocityBoostRef.current : 0;
        const effectiveProgress = clamp(progressForSections + velocityBoost, 0, 1);

        const newSection = clamp(Math.round(effectiveProgress * maxIdx), 0, maxIdx);

        // Reset after applying once, otherwise the boost would accumulate.
        velocityBoostRef.current = 0;

        setActiveSection((prev) => (prev === newSection ? prev : newSection));
      };

      const onScroll = () => {
        if (tickingRef.current) return;
        tickingRef.current = true;

        // Compute instantaneous velocity (px/ms) so we can bias the boundary decision a bit.
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const y = window.scrollY;
        const lastY = lastScrollYRef.current;
        const lastT = lastScrollTimeRef.current || now;
        const dy = y - lastY;
        const dt = Math.max(1, now - lastT);
        lastScrollYRef.current = y;
        lastScrollTimeRef.current = now;

        if (isProgrammaticScrollRef.current) {
          // Manual slider navigation should not get “velocity-biased” transitions.
          velocityBoostRef.current = 0;
        } else {
          const velocityPxPerMs = dy / dt;
          const velocityNorm = clamp(Math.abs(velocityPxPerMs) / 2.5, 0, 1);

          // Only a very small boost is needed to keep section boundaries feeling responsive.
          const maxBoost = 0.02;
          const boost = (dy === 0 ? 0 : Math.sign(dy)) * velocityNorm * maxBoost;
          velocityBoostRef.current = boost;
        }

        requestAnimationFrame(() => {
          tickingRef.current = false;
          updateFromScroll();
        });
      };

      // Measure after layout + fonts settle.
      const raf = requestAnimationFrame(() => {
        recalcBounds();
        velocityBoostRef.current = 0;
        lastScrollYRef.current = window.scrollY;
        lastScrollTimeRef.current =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
        updateFromScroll();
      });

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', recalcBounds);

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', recalcBounds);
      };
    }, [sectionScrollHeight, postSectionScrollHeight, isMobileViewport]);

    const scrollToSectionIndex = (targetIdx) => {
      const { start, sectionEnd } = scrollBoundsRef.current;
      if (!Number.isFinite(start) || !Number.isFinite(sectionEnd)) return;
      if (sectionEnd <= 0 && start <= 0) return;

      const maxIdx = sections.length - 1;
      const idx = clamp(targetIdx, 0, maxIdx);

      const sectionDenom = sectionEnd - start;
      if (sectionDenom <= 0) return;

      // Invert the “assisted” progress mapping used in updateFromScroll().
      const mappedProgressTarget = idx / maxIdx;
      const scrolledFromStartCandidate1 = mappedProgressTarget * sectionDenom;

      const scrolledFromStart =
        scrolledFromStartCandidate1 <= 200
          ? scrolledFromStartCandidate1
          : (mappedProgressTarget * sectionDenom + 40) / 1.2;

      // Prevent overshooting into the CTA handoff region.
      const safeScrolledFromStart = clamp(scrolledFromStart, 0, sectionDenom);
      const targetY = start + safeScrolledFromStart;

      isProgrammaticScrollRef.current = true;
      velocityBoostRef.current = 0;
      lastScrollYRef.current = targetY;
      lastScrollTimeRef.current =
        typeof performance !== 'undefined' ? performance.now() : Date.now();

      window.scrollTo({ top: targetY, behavior: 'auto' });

      // Re-enable velocity bias after the scroll event burst settles.
      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 250);
    };

    const isInSections = handoffProgress < 1;
    const scrollExperienceTransform = `translateY(-${handoffProgress * 100}vh)`;

    const openCalendlyRequestDemoModal = () => setIsCalendlyOpen(true);
    const openCalendlyTrainingSlotModal = () => setIsCalendlyTrainingSlotOpen(true);

    const setMobileSection = (targetIdx) => {
      const totalSections = sections.length;
      const wrappedIndex = ((targetIdx % totalSections) + totalSections) % totalSections;
      setActiveSection(wrappedIndex);
    };

    const handleMobileSwipeStart = (event) => {
      if (!isMobileViewport) return;
      touchStartXRef.current = event.touches[0].clientX;
      touchEndXRef.current = null;
    };

    const handleMobileSwipeMove = (event) => {
      if (!isMobileViewport) return;
      touchEndXRef.current = event.touches[0].clientX;
    };

    const handleMobileSwipeEnd = () => {
      if (!isMobileViewport) return;
      if (touchStartXRef.current == null || touchEndXRef.current == null) return;

      const deltaX = touchStartXRef.current - touchEndXRef.current;
      const swipeThreshold = 4;

      if (deltaX > swipeThreshold) {
        setMobileSection(activeSection + 1);
      } else if (deltaX < -swipeThreshold) {
        setMobileSection(activeSection - 1);
      }

      touchStartXRef.current = null;
      touchEndXRef.current = null;
    };

    useEffect(() => {
      if (!isMobileViewport) return undefined;

      const autoplayId = window.setInterval(() => {
        setActiveSection((prev) => (prev + 1) % sections.length);
      }, 3500);

      return () => window.clearInterval(autoplayId);
    }, [isMobileViewport]);

    return (
      <div className="min-h-screen bg-white" data-testid="landing-page">
        <div className="bp-desktop-only">
          <ProductHeader
            scrolled={scrolled}
            alwaysBorder
            rightBrand={{
              logoSrc: '/Tchrr.svg',
              alt: 'Tchrr AI logo',
              title: '',
              to: '/tchrr',
            }}
            links={[
            {label: 'Brochure', 
              href:"https://drive.google.com/file/d/1Uif8K1hm6Y0iwg7Dqj0QB5euYXI0O3Xk/view?usp=drive_link",
              target: '_blank',
              rel: 'noopener noreferrer',
              }, 
            {label: 'Specs', 
              href:"https://drive.google.com/file/d/1DGdikD1QUHHQEltdlYAMkl4PPUpMZwZv/view?usp=sharing",
              target: '_blank',
              rel: 'noopener noreferrer',
              }
            ]}
            action={{label: 'Book a Training Slot', variant: 'outline', onClick: openCalendlyTrainingSlotModal}}
            highlightedAction={{
              label: 'Request a Demo',
              variant: 'default',
              onClick: openCalendlyRequestDemoModal,
            }}
          />
        </div>

        <div className="bp-mobile-only">
          <MobileProductHeader
            scrolled={scrolled}
            rightBrand={{
              logoSrc: '/Tchrr.svg',
              alt: 'Tchrr AI logo',
              title: '',
              to: '/tchrr',
            }}
            links={[
              {
                label: 'Brochure',
                href: 'https://drive.google.com/file/d/1Uif8K1hm6Y0iwg7Dqj0QB5euYXI0O3Xk/view?usp=drive_link',
                target: '_blank',
                rel: 'noopener noreferrer',
              },
              {
                label: 'Specs',
                href: 'https://drive.google.com/file/d/1DGdikD1QUHHQEltdlYAMkl4PPUpMZwZv/view?usp=sharing',
                target: '_blank',
                rel: 'noopener noreferrer',
              },
            ]}
            action={{ label: 'Book a Training Slot', variant: 'outline', onClick: openCalendlyTrainingSlotModal }}
            highlightedAction={{
              label: 'Request a Demo',
              variant: 'default',
              onClick: openCalendlyRequestDemoModal,
            }}
          />
        </div>

        <div className="bp-desktop-only">
          {/* Main scroll container */}
          <div ref={containerRef} className="relative" style={{ height: `${totalScrollHeight}px` }}>
            {/* Fixed content layer - only visible during scroll sections */}
            {isInSections && (
              <div
                className="fixed inset-0 top-0 z-10 pointer-events-none tchrr-fixed-layer-bg"
                style={{ height: '100vh', transform: scrollExperienceTransform }}
              >
                <div className="h-full max-w-7xl mx-auto px-6 pt-8 lg:px-8 lg:pt-10 flex gap-8 xl:gap-12 pointer-events-auto">
                  {/* Left side - Fixed text that changes */}
                  <div className="w-full lg:w-1/2 flex items-center justify-center lg:justify-start">
                    <div className="w-full max-w-xl pt-20 lg:pt-0">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeSection}
                          initial={{ opacity: 0, y: 30, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -20, scale: 0.98 }}
                          transition={{
                            duration: 0.4,
                            ease: [0.25, 0.46, 0.45, 0.94],
                            y: { type: 'spring', stiffness: 300, damping: 25 },
                          }}
                        >
                          <h2
                            className={`font-heading font-medium text-gray-900 leading-[1.1] tracking-tight mb-4 sm:mb-6 whitespace-pre-line ${
                              activeSection === 0
                                ? 'text-2xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl'
                                : 'text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl'
                            }`}
                          >
                            {sections[activeSection].text}
                          </h2>
                          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 leading-relaxed max-w-md">
                            {sections[activeSection].subtext}
                          </p>
                        </motion.div>
                      </AnimatePresence>

                      <div className="flex gap-2 mt-8 sm:mt-12">
                        {sections.map((_, index) => (
                          <motion.div
                            key={index}
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                              index === activeSection ? 'w-8 bg-gray-900' : 'w-1.5 bg-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="tchrr-desktop-canvas flex w-1/2 pt-40 items-top justify-center">
                    <div className="w-full max-w-[500px] lg:max-w-[520px] xl:max-w-[580px]">
                      <WhiteboardCanvas activeSection={activeSection} mode="desktop" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bp-mobile-only min-h-screen px-2 pt-32 pb-8 flex items-start">
          <div
            className="w-full max-w-[340px] sm:max-w-md mx-auto min-h-[calc(100vh-8rem)] flex flex-col justify-center"
            onTouchStart={handleMobileSwipeStart}
            onTouchMove={handleMobileSwipeMove}
            onTouchEnd={handleMobileSwipeEnd}
          >
            <div className="relative overflow-visible">
              <div className="mx-auto w-[90%] sm:w-[92%]">
                <WhiteboardCanvas
                  activeSection={activeSection}
                  mode="mobile"
                  onMobileSectionChange={setMobileSection}
                  disableTouchSwipe
                />
              </div>

              {/* <button
                type="button"
                aria-label="Previous slide"
                className="absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-16 flex items-center justify-center text-slate-900"
                onClick={() => setMobileSection(activeSection - 1)}
              >
                <ChevronLeft size={30} />
              </button>

              <button
                type="button"
                aria-label="Next slide"
                className="absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-16 flex items-center justify-center text-slate-900"
                onClick={() => setMobileSection(activeSection + 1)}
              >
                <ChevronRight size={30} />
              </button> */}
            </div>

            <div className="mt-7 text-center flex-1 flex flex-col justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSection}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.28 }}
                >
                  <h2
                    className={`font-heading font-medium text-gray-900 leading-[1.08] tracking-tight whitespace-pre-line ${
                      activeSection === 0 ? 'text-[1.95rem] sm:text-[2.2rem]' : 'text-[1.85rem] sm:text-[2.1rem]'
                    }`}
                  >
                    {sections[activeSection].text}
                  </h2>
                  <p className="mt-3 text-base sm:text-lg text-gray-600 leading-relaxed">
                    {sections[activeSection].subtext}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-center gap-2 mt-8">
                {sections.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === activeSection ? 'w-8 bg-gray-900' : 'w-1.5 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section - normal document flow for consistent scroll handoff */}
        <div
          className="relative z-[5] bg-[#F8FAFC] pt-6 sm:pt-8 lg:pt-20"
        >
          <div className="hidden min-h-[20vh] lg:block" />
          <CTASection onRequestDemo={openCalendlyRequestDemoModal} onBookTrainingSlot={openCalendlyTrainingSlotModal} />
        </div>

        <Footer />

        <RequestDemoDialog open={isCalendlyOpen} onOpenChange={setIsCalendlyOpen} />

        <TrainingSlotDialog
          open={isCalendlyTrainingSlotOpen}
          onOpenChange={setIsCalendlyTrainingSlotOpen}
        />

        <Sonner position="bottom-right" />
      </div>
    );
  }

